import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { api } from '../services/api';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { SUPPORTED_LANGUAGES, changeLanguage, type SupportedLanguage } from '../i18n';

interface Tenant {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  city: string | null;
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tenant search state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const tenantSearchRef = useRef<HTMLDivElement>(null);

  // Form state - initialize from user
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    company: '',
    position: '',
    bio: '',
    tenant_id: null as string | null,
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    website: '',
    linkedin_url: '',
    xing_url: '',
  });

  // Sync form data when user changes (important: after save/checkAuth)
  // Only sync when NOT in editing mode to avoid overwriting user's unsaved changes
  useEffect(() => {
    if (user && !isEditing) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        company: user.company || '',
        position: user.position || '',
        bio: user.bio || '',
        tenant_id: user.tenant_id || null,
        street: user.street || '',
        house_number: user.house_number || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        country: user.country || 'Deutschland',
        website: user.website || '',
        linkedin_url: user.linkedin_url || '',
        xing_url: user.xing_url || '',
      });
    }
  }, [user, isEditing]);

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Load tenants for dropdown
  useEffect(() => {
    const loadTenants = async () => {
      setIsLoadingTenants(true);
      try {
        const response = await api.get<{ data: Tenant[] }>(`/tenants/search?q=${tenantSearch}&limit=10`);
        setTenants(response.data);
      } catch (error) {
        console.error('Error loading tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };

    const debounce = setTimeout(loadTenants, 300);
    return () => clearTimeout(debounce);
  }, [tenantSearch]);

  // Close tenant dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tenantSearchRef.current && !tenantSearchRef.current.contains(event.target as Node)) {
        setShowTenantDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected tenant info
  const selectedTenant = user?.tenant || tenants.find(t => t.id === formData.tenant_id);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      // Also update the legacy name field for backward compatibility
      const name = [formData.first_name, formData.last_name].filter(Boolean).join(' ');
      await api.put('/users/me', { ...formData, name });
      await checkAuth(); // Refresh user data - this triggers the useEffect to sync formData
      setIsEditing(false);
      setMessage({ type: 'success', text: t('settings:profileSaved') });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('settings:saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: t('settings:passwordsNoMatch') });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: t('settings:passwordTooShort') });
      return;
    }

    setIsSaving(true);
    setMessage(null);
    try {
      await api.post('/users/me/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: t('settings:passwordChanged') });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('settings:passwordChangeError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 150 * 1024) {
      setMessage({ type: 'error', text: t('settings:avatarTooLarge') });
      return;
    }

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setMessage({ type: 'error', text: t('settings:avatarWrongFormat') });
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('avatar', file);

    setIsSaving(true);
    setMessage(null);
    try {
      const response = await fetch('/api/users/me/avatar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${api.getAccessToken()}`,
        },
        credentials: 'include',
        body: formDataUpload,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('settings:uploadError'));
      }

      await checkAuth();
      setMessage({ type: 'success', text: t('settings:avatarUpdated') });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('settings:uploadError') });
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = () => {
    const first = user?.first_name;
    const last = user?.last_name;
    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  const getDisplayName = () => {
    const first = user?.first_name;
    const last = user?.last_name;
    if (first || last) {
      return [first, last].filter(Boolean).join(' ');
    }
    return user?.name || t('settings:noName');
  };

  const cancelEditing = () => {
    setIsEditing(false);
    // Reset form to current user data
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        company: user.company || '',
        position: user.position || '',
        bio: user.bio || '',
        tenant_id: user.tenant_id || null,
        street: user.street || '',
        house_number: user.house_number || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        country: user.country || 'Deutschland',
        website: user.website || '',
        linkedin_url: user.linkedin_url || '',
        xing_url: user.xing_url || '',
      });
    }
    setTenantSearch('');
  };

  // Field display component for read mode
  const DisplayField = ({ label, value, isLink = false }: { label: string; value: string | null | undefined; isLink?: boolean }) => (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value ? (
          isLink ? (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
              {value.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              <ExternalLinkIcon />
            </a>
          ) : (
            value
          )
        ) : (
          <span className="text-gray-400">–</span>
        )}
      </dd>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <PageHeader
          icon={
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
          title={t('settings:profile')}
          subtitle={t('settings:profileSettings.manageSettings')}
          breadcrumbs={[{ label: 'Dashboard', href: '/' }]}
        />

        {/* Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-60 hover:opacity-100">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Profile Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative group flex-shrink-0">
                <button
                  onClick={handleAvatarClick}
                  className="relative focus:outline-none focus:ring-4 focus:ring-white/30 rounded-full"
                  disabled={isSaving}
                >
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={getDisplayName()}
                      className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-xl"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-xl">
                      <span className="text-blue-600 text-2xl font-bold">{getInitials()}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium flex items-center gap-1">
                      <CameraIcon className="w-4 h-4" />
                      {t('settings:change')}
                    </span>
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>

              {/* User Info */}
              <div className="text-center sm:text-left text-white flex-1">
                <h1 className="text-2xl font-bold">{getDisplayName()}</h1>
                <p className="text-blue-100 mt-1">{user?.email}</p>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
                  <span className="text-blue-200 text-xs font-mono bg-white/10 px-2 py-0.5 rounded">
                    {user?.display_id}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    user?.role === 'admin' ? 'bg-purple-500/30 text-purple-100' :
                    user?.role === 'editor' ? 'bg-blue-500/30 text-blue-100' :
                    'bg-gray-500/30 text-gray-100'
                  }`}>
                    {t(`settings:roles.${user?.role}`)}
                  </span>
                </div>
              </div>

              {/* Edit Button */}
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-shrink-0 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  <PencilIcon className="w-4 h-4" />
                  {t('settings:edit')}
                </button>
              )}
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-6">
            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-8">
                {/* Personal Data Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:profileSettings.personalData')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                      label={t('settings:profileSettings.firstName')}
                      value={formData.first_name}
                      onChange={(v) => setFormData({ ...formData, first_name: v })}
                      placeholder="Max"
                    />
                    <InputField
                      label={t('settings:profileSettings.lastName')}
                      value={formData.last_name}
                      onChange={(v) => setFormData({ ...formData, last_name: v })}
                      placeholder="Mustermann"
                    />
                    <InputField
                      label={t('settings:profileSettings.email')}
                      value={user?.email || ''}
                      disabled
                    />
                    <InputField
                      label={t('settings:profileSettings.phone')}
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v })}
                      placeholder="+49 123 456789"
                      type="tel"
                    />
                    <InputField
                      label={t('settings:profileSettings.company')}
                      value={formData.company}
                      onChange={(v) => setFormData({ ...formData, company: v })}
                      placeholder="Meine Firma GmbH"
                    />
                    <InputField
                      label={t('settings:profileSettings.position')}
                      value={formData.position}
                      onChange={(v) => setFormData({ ...formData, position: v })}
                      placeholder="Marketing Manager"
                    />
                    {/* Tenant Dropdown */}
                    <div ref={tenantSearchRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:profileSettings.tenant')}</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedTenant ? selectedTenant.name : tenantSearch}
                          onChange={(e) => {
                            setTenantSearch(e.target.value);
                            setFormData({ ...formData, tenant_id: null });
                            setShowTenantDropdown(true);
                          }}
                          onFocus={() => setShowTenantDropdown(true)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder={t('settings:search')}
                        />
                        {selectedTenant && (
                          <button
                            onClick={() => {
                              setFormData({ ...formData, tenant_id: null });
                              setTenantSearch('');
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        )}
                        {showTenantDropdown && !selectedTenant && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {isLoadingTenants ? (
                              <div className="px-4 py-3 text-gray-500 text-sm">{t('settings:loading')}</div>
                            ) : tenants.length === 0 ? (
                              <div className="px-4 py-3 text-gray-500 text-sm">
                                {tenantSearch ? t('settings:noCompanyFound') : t('settings:enterSearchTerm')}
                              </div>
                            ) : (
                              tenants.map((tenant) => (
                                <button
                                  key={tenant.id}
                                  onClick={() => {
                                    setFormData({ ...formData, tenant_id: tenant.id });
                                    setTenantSearch('');
                                    setShowTenantDropdown(false);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                                >
                                  <div
                                    className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                    style={{ backgroundColor: tenant.primary_color || '#3B82F6' }}
                                  >
                                    {tenant.name.slice(0, 1).toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{tenant.name}</div>
                                    {tenant.city && <div className="text-xs text-gray-500">{tenant.city}</div>}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:profileSettings.bio')}</label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                        placeholder={t('settings:profileSettings.bioPlaceholder')}
                      />
                    </div>
                  </div>
                </section>

                {/* Address Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:address.title')}
                  </h3>
                  <div className="grid grid-cols-4 sm:grid-cols-12 gap-4">
                    <div className="col-span-3 sm:col-span-9">
                      <InputField
                        label={t('settings:address.street')}
                        value={formData.street}
                        onChange={(v) => setFormData({ ...formData, street: v })}
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-3">
                      <InputField
                        label={t('settings:address.houseNumber')}
                        value={formData.house_number}
                        onChange={(v) => setFormData({ ...formData, house_number: v })}
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-3">
                      <InputField
                        label={t('settings:address.postalCode')}
                        value={formData.postal_code}
                        onChange={(v) => setFormData({ ...formData, postal_code: v })}
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-5">
                      <InputField
                        label={t('settings:address.city')}
                        value={formData.city}
                        onChange={(v) => setFormData({ ...formData, city: v })}
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-4">
                      <InputField
                        label={t('settings:address.country')}
                        value={formData.country}
                        onChange={(v) => setFormData({ ...formData, country: v })}
                      />
                    </div>
                  </div>
                </section>

                {/* Online Profiles Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <GlobeIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:onlineProfiles.title')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <InputField
                        label={t('settings:onlineProfiles.website')}
                        value={formData.website}
                        onChange={(v) => setFormData({ ...formData, website: v })}
                        placeholder="https://example.com"
                        type="url"
                      />
                    </div>
                    <InputField
                      label={t('settings:onlineProfiles.linkedin')}
                      value={formData.linkedin_url}
                      onChange={(v) => setFormData({ ...formData, linkedin_url: v })}
                      placeholder="https://linkedin.com/in/..."
                      type="url"
                    />
                    <InputField
                      label={t('settings:onlineProfiles.xing')}
                      value={formData.xing_url}
                      onChange={(v) => setFormData({ ...formData, xing_url: v })}
                      placeholder="https://xing.com/profile/..."
                      type="url"
                    />
                  </div>
                </section>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={cancelEditing}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                    disabled={isSaving}
                  >
                    {t('settings:cancel')}
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors inline-flex items-center gap-2"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <SpinnerIcon className="w-4 h-4 animate-spin" />
                        {t('settings:saving')}
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        {t('settings:save')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="space-y-8">
                {/* Personal Data Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:profileSettings.personalData')}
                  </h3>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    <DisplayField label={t('settings:profileSettings.firstName')} value={user?.first_name} />
                    <DisplayField label={t('settings:profileSettings.lastName')} value={user?.last_name} />
                    <DisplayField label={t('settings:profileSettings.email')} value={user?.email} />
                    <DisplayField label={t('settings:profileSettings.phone')} value={user?.phone} />
                    <DisplayField label={t('settings:profileSettings.company')} value={user?.company} />
                    <DisplayField label={t('settings:profileSettings.position')} value={user?.position} />
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('settings:profileSettings.tenant')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {selectedTenant ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: selectedTenant.primary_color || '#3B82F6' }}
                            >
                              {selectedTenant.name.slice(0, 1).toUpperCase()}
                            </div>
                            <span>{selectedTenant.name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">–</span>
                        )}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">{t('settings:profileSettings.bio')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                        {user?.bio || <span className="text-gray-400">–</span>}
                      </dd>
                    </div>
                  </dl>
                </section>

                {/* Address Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:address.title')}
                  </h3>
                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
                    <div className="col-span-2 sm:col-span-3">
                      <dt className="text-sm font-medium text-gray-500">{t('settings:address.street')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {user?.street && user?.house_number
                          ? `${user.street} ${user.house_number}`
                          : user?.street || <span className="text-gray-400">–</span>}
                      </dd>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">{t('settings:address.postalCode')} / {t('settings:address.city')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {user?.postal_code || user?.city
                          ? `${user.postal_code || ''} ${user.city || ''}`.trim()
                          : <span className="text-gray-400">–</span>}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('settings:address.country')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {user?.country || <span className="text-gray-400">–</span>}
                      </dd>
                    </div>
                  </dl>
                </section>

                {/* Online Profiles Section */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <GlobeIcon className="w-4 h-4 text-gray-400" />
                    {t('settings:onlineProfiles.title')}
                  </h3>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="sm:col-span-2">
                      <DisplayField label={t('settings:onlineProfiles.website')} value={user?.website} isLink />
                    </div>
                    <DisplayField label={t('settings:onlineProfiles.linkedin')} value={user?.linkedin_url} isLink />
                    <DisplayField label={t('settings:onlineProfiles.xing')} value={user?.xing_url} isLink />
                  </dl>
                </section>
              </div>
            )}
          </div>
        </div>

        {/* Security Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-gray-400" />
              {t('settings:security')}
            </h3>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {t('settings:securitySettings.changePassword')}
              </button>
            )}
          </div>

          {!isChangingPassword ? (
            <p className="text-sm text-gray-500">{t('settings:securitySettings.passwordSecure')}</p>
          ) : (
            <div className="space-y-4 max-w-md">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:securitySettings.currentPassword')}</label>
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showPasswords.current} />
                </button>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:securitySettings.newPassword')}</label>
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showPasswords.new} />
                </button>
                <p className="text-xs text-gray-500 mt-1">{t('settings:securitySettings.minCharacters')}</p>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:securitySettings.confirmPassword')}</label>
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showPasswords.confirm} />
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                  disabled={isSaving}
                >
                  {t('settings:cancel')}
                </button>
                <button
                  onClick={handleChangePassword}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors"
                  disabled={isSaving || !passwordData.currentPassword || !passwordData.newPassword}
                >
                  {isSaving ? t('settings:saving') : t('settings:securitySettings.changePassword')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Language Card */}
        <LanguageSelector />
      </div>
    </Layout>
  );
}

// Language Selector Component
function LanguageSelector() {
  const { t, i18n } = useTranslation(['settings']);
  const [isChanging, setIsChanging] = useState(false);

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    if (lang === i18n.language) return;

    setIsChanging(true);
    try {
      await changeLanguage(lang, true);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 p-6">
      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
        <LanguageIcon className="w-4 h-4 text-gray-400" />
        {t('settings:languageSettings.title')}
      </h3>

      <div className="space-y-3">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            disabled={isChanging}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
              i18n.language === lang.code
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            } ${isChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-2xl">{lang.flag}</span>
            <span className={`font-medium ${
              i18n.language === lang.code ? 'text-blue-700' : 'text-gray-700'
            }`}>
              {lang.name}
            </span>
            {i18n.language === lang.code && (
              <CheckIcon className="w-5 h-5 text-blue-600 ml-auto" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// Reusable Input Field Component
function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
          disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
        }`}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={!onChange}
      />
    </div>
  );
}

// Icons
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function LanguageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function EyeIcon({ show }: { show: boolean }) {
  if (show) {
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}
